import { useMemo, useState } from 'react';
import { apiGet } from './api';
import { mln } from './format';
import { useUser } from './user-context';
import { useData } from './useData';
import { DealModal } from './DealModal';
import { downloadCSV, printRegister, registerMoney, type Col } from './export-utils';

type Ev = {
  date: string;
  kind: string;
  type: string;
  label: string;
  client: string;
  amount: number | null;
  dealId: number;
};
type PlanData = { today: string; events: Ev[] };

const EV_COLOR: Record<string, string> = {
  paid: 'grn',
  plan: 'gold',
  partial: 'gold',
  over: 'red',
  prod: 'or',
  otk: 'vi',
  ship: 'cy',
};
const EV_ICON: Record<string, string> = { pay: '💰', prod: '🏭', otk: '🔎', ship: '🚚' };
const TYPE_LABEL: Record<string, string> = {
  paid: 'оплачено',
  plan: 'к оплате',
  partial: 'частично',
  over: 'просрочено',
  prod: 'старт цеха',
  otk: 'ОТК',
  ship: 'отгрузка',
};
const MONTHS = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];
const DOW = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

// Фильтр событий по типу (kind). По умолчанию включены все.
const KIND_FILTERS = [
  { k: 'pay', label: 'Платежи', ico: '💰' },
  { k: 'prod', label: 'Производство', ico: '🏭' },
  { k: 'otk', label: 'ОТК', ico: '🔎' },
  { k: 'ship', label: 'Отгрузки', ico: '🚚' },
] as const;

const CAL_COLS: Col<Ev>[] = [
  { header: 'Дата', value: (e) => e.date },
  { header: 'Тип', value: (e) => TYPE_LABEL[e.type] ?? e.type },
  { header: 'Событие', value: (e) => e.label },
  { header: 'Клиент', value: (e) => e.client },
  {
    header: 'Сумма, ₸',
    value: (e) => e.amount ?? '',
    print: (e) => registerMoney(e.amount),
    align: 'r',
  },
];

const planEvents = () => apiGet<PlanData>('/api/plan/events');

export function PlanScreen() {
  const user = useUser();
  const { data, refresh } = useData('plan', planEvents);
  const [offset, setOffset] = useState(0); // смещение месяца от текущего
  const [openId, setOpenId] = useState<number | null>(null);
  const [active, setActive] = useState<Set<string>>(() => new Set(KIND_FILTERS.map((f) => f.k)));

  function toggleKind(k: string) {
    setActive((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  }

  // Видимые события (после фильтра по типу) — общий источник для сетки, агенды и экспорта.
  const visible = useMemo(
    () => (data?.events ?? []).filter((e) => active.has(e.kind)),
    [data, active],
  );
  const byDate = useMemo(() => {
    const m = new Map<string, Ev[]>();
    for (const e of visible) (m.get(e.date) ?? m.set(e.date, []).get(e.date)!).push(e);
    return m;
  }, [visible]);

  if (!data) return <p className="muted">Загрузка…</p>;

  const today = new Date(data.today);
  const view = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const year = view.getFullYear();
  const month = view.getMonth();
  const first = new Date(year, month, 1);
  const startDow = (first.getDay() + 6) % 7; // Пн=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const fmt = (d: number) =>
    `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const cells: (number | null)[] = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  // агенда: сначала просрочка (самое срочное, в т.ч. в прошлом), затем ближайшие
  const byDateAsc = (a: Ev, b: Ev) => a.date.localeCompare(b.date);
  const overdue = visible.filter((e) => e.type === 'over').sort(byDateAsc);
  const upcoming = visible.filter((e) => e.type !== 'over' && e.date >= data.today).sort(byDateAsc);
  const agenda = [...overdue, ...upcoming].slice(0, 10);

  // «Ожидается в этом месяце»: остаток к получению по неоплаченным платежам
  // просматриваемого месяца (сумма amount для pay-событий, кроме уже оплаченных).
  const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`;
  const expectedThisMonth = visible
    .filter(
      (e) =>
        e.kind === 'pay' && e.type !== 'paid' && e.amount != null && e.date.startsWith(monthPrefix),
    )
    .reduce((s, e) => s + (e.amount ?? 0), 0);

  const exportRows = [...visible].sort(byDateAsc);
  const filterNote = (): string => {
    const on = KIND_FILTERS.filter((f) => active.has(f.k));
    return on.length === KIND_FILTERS.length
      ? 'Все события'
      : 'Только: ' + on.map((f) => f.label).join(', ');
  };
  const exportCSV = () => downloadCSV('календарь', CAL_COLS, exportRows);
  const printReg = () => {
    const total = exportRows.reduce((s, e) => s + (e.amount ?? 0), 0);
    printRegister({
      title: 'Реестр событий календаря',
      subtitle: filterNote(),
      cols: CAL_COLS,
      rows: exportRows,
      totals: ['Итого', '', '', '', registerMoney(total)],
    });
  };

  return (
    <div className="scr">
      <div className="scr-head">
        <h2>Календарь завода</h2>
        <span className="muted">платежи · производство · ОТК · отгрузки</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {expectedThisMonth > 0 && (
            <span className="muted small">
              Ожидается в {MONTHS[month]}:{' '}
              <b style={{ color: 'var(--gold)' }}>{mln(expectedThisMonth)} ₸</b>
            </span>
          )}
          <button
            className="btn ghost sm"
            disabled={exportRows.length === 0}
            onClick={exportCSV}
            title="Скачать CSV (Excel)"
          >
            ⬇ CSV
          </button>
          <button
            className="btn ghost sm"
            disabled={exportRows.length === 0}
            onClick={printReg}
            title="Печатный реестр событий"
          >
            🖨 Реестр
          </button>
        </div>
      </div>

      <div className="filters">
        {KIND_FILTERS.map((f) => (
          <button
            key={f.k}
            className={'btn sm' + (active.has(f.k) ? '' : ' ghost')}
            onClick={() => toggleKind(f.k)}
            title={active.has(f.k) ? 'Скрыть на календаре' : 'Показать на календаре'}
          >
            {f.ico} {f.label}
          </button>
        ))}
        <span className="muted small filter-count">{visible.length} событий</span>
      </div>

      <div className="cal-head">
        <button className="icon-btn" onClick={() => setOffset(offset - 1)}>
          ‹
        </button>
        <b style={{ minWidth: 160, textAlign: 'center' }}>
          {MONTHS[month]} {year}
        </b>
        <button className="icon-btn" onClick={() => setOffset(offset + 1)}>
          ›
        </button>
        <button className="btn ghost sm" onClick={() => setOffset(0)}>
          Сегодня
        </button>
        <span className="spacer" style={{ flex: 1 }} />
        <span className="chip grn">оплачено</span>
        <span className="chip gold">платёж</span>
        <span className="chip red">просрочка</span>
        <span className="chip or">старт цеха</span>
        <span className="chip vi">ОТК</span>
        <span className="chip cy">отгрузка</span>
      </div>

      <div className="cal-grid glass">
        {DOW.map((d) => (
          <div key={d} className="cal-dow">
            {d}
          </div>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <div key={i} className="cal-cell empty" />;
          const ds = fmt(d);
          const evs = byDate.get(ds) ?? [];
          return (
            <div key={i} className={'cal-cell' + (ds === data.today ? ' today' : '')}>
              <div className="cal-day">{d}</div>
              {evs.slice(0, 3).map((e, k) => (
                <div
                  key={k}
                  className={'cal-ev ' + (EV_COLOR[e.type] ?? '')}
                  title={e.client + ' · ' + e.label}
                  onClick={() => setOpenId(e.dealId)}
                >
                  {e.amount != null ? mln(e.amount) : e.label}
                </div>
              ))}
              {evs.length > 3 && <div className="cal-more">+{evs.length - 3} ещё</div>}
            </div>
          );
        })}
      </div>

      <div className="section-t">Ближайшие события</div>
      <div className="glass" style={{ padding: 6 }}>
        {agenda.length === 0 && (
          <p className="muted" style={{ padding: 10 }}>
            Событий нет.
          </p>
        )}
        {agenda.map((e, i) => (
          <div
            className="row"
            key={i}
            style={{ cursor: 'pointer', padding: '10px 12px' }}
            onClick={() => setOpenId(e.dealId)}
          >
            <div className="att-ic">{EV_ICON[e.kind] ?? '•'}</div>
            <div className="grow">
              <div className="rname">
                {e.label} · {e.client}
              </div>
              <div className="rmeta">
                {e.date}
                {e.type === 'over' ? ' · просрочен/к выставлению' : ''}
              </div>
            </div>
            {e.amount != null && <div className="price">{mln(e.amount)} ₸</div>}
            <span className={'chip ' + (EV_COLOR[e.type] ?? '')}>
              {TYPE_LABEL[e.type] ?? e.type}
            </span>
          </div>
        ))}
      </div>

      {openId != null && (
        <DealModal
          dealId={openId}
          screens={user.screens}
          onClose={() => setOpenId(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}
